import { ESdChangeReason, IAction, IActionParameter, IKType, ISymbol, IWorkFlow } from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import { SymbolPicker } from "./symbol-picker";

export interface IWFGenericProcessesSymbolsGroupState {
  fromSingleProcess: boolean;
  action: IAction;
  selectedProcess: IWorkFlow | undefined;
}

export interface IWFGenericProcessesSymbolsGroupProps {
  action: IAction;
  selectedProcess: IWorkFlow | undefined;
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
  onActionParameterSelected?: (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => void;
}

class WFGenericProcessesSymbolsGroup extends PureSdComponent<
  IWFGenericProcessesSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IWFGenericProcessesSymbolsGroupState
> {
  constructor(props: IWFGenericProcessesSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      fromSingleProcess: !!this.props.selectedProcess,
      action: this.props.action,
      selectedProcess: undefined
    };
  }

  componentDidMount(): void {
    this.setState({
      selectedProcess: this.props.selectedProcess
    });
  }

  toggleFromSingleProcess = (value) => {
    console.log("toggleFromSingleProcess called, param:", value);
    if (value === false) {
      const { action } = this.state;
      const process = action?.parameters.find((p) => p.name === "ProcessDefinitionId");
      if (process) process.symbol.fixedValue = "";
      const valuesToUpdate = {
        actionParameters: this.state.action.parameters,
        actionReturnTypes: {
          elements: []
        } as unknown as IKType
      } as IActionPanelStatesCanBeUpdatedFromChild;
      this.props.updateValues(valuesToUpdate, ESdChangeReason.ActionDescriptionChanged);
      this.setState({
        selectedProcess: undefined
      });
    }
    this.setState({ fromSingleProcess: value });
  };

  handleProcessChange = (value: string | null): void => {
    const { action } = this.state;
    const process = action?.parameters.find((p) => p.name === "ProcessDefinitionId");
    // The kit reports a cleared single select as `null`; antd reported `undefined`, and a
    // `fixedValue` of `null` would be serialised into the screen where nothing was written
    // before. `undefined` keeps the key out of the JSON, as it was.
    if (process) process.symbol.fixedValue = value ?? undefined;
    const valuesToUpdate = {
      actionParameters: this.state.action.parameters
    } as IActionPanelStatesCanBeUpdatedFromChild;
    this.props.updateValues(valuesToUpdate, ESdChangeReason.ActionDescriptionChanged);
    this.setState({
      selectedProcess: this.props.sd.workFlowList.find((wf) => wf.id === value)
    });
  };

  handlePropertySelection = (value) => {
    const { action } = this.state;

    const actionDynamicReturnTypes = {
      elements: []
    } as unknown as IKType;

    const valuesToUpdate = {
      actionParameters: this.state.action.parameters,
      actionReturnTypes: actionDynamicReturnTypes
    } as IActionPanelStatesCanBeUpdatedFromChild;

    this.props.updateValues(valuesToUpdate, ESdChangeReason.ActionDescriptionChanged);
  };

  render() {
    const processOptions: KuikaSelectboxOption[] = (this.props.sd.workFlowList ?? []).map(
      (wf: IWorkFlow): KuikaSelectboxOption => ({ key: wf.id, value: wf.id, label: wf.name })
    );

    return (
      <>
        <div style={{ marginTop: 12, marginBottom: 12 }}>
          <KuikaSwitch
            checked={this.state.fromSingleProcess}
            disabled={false}
            label="Select from single process"
            onChange={(checked) => {
              this.toggleFromSingleProcess(checked);
            }}
          />
        </div>
        <div style={{ marginBottom: 12, display: this.state.fromSingleProcess ? "block" : "none" }}>
          <div className="kuika_symbol-picker__managed-db">
            <div style={{ width: "100%" }}>
              <div>
                <label className="kuika_properties_label_text">Process</label>
                <div>
                  <KuikaSelectbox
                    style={{ width: "100%" }}
                    searchable
                    className="kuika__action-parameters__item-select"
                    placeholder="Select a process"
                    allowClear={true}
                    onChange={this.handleProcessChange}
                    options={processOptions}
                    value={this.state.selectedProcess?.id ?? null}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {this.props.action.parameters
          ?.filter((p) => !["ProcessDefinitionId"].includes(p.name))
          .filter((p) =>
            p.isOptionalParameter === true ? (p.symbol.fixedValue ? true : p.isExternalVisible === true) : true
          )
          ?.map((p, index) => {
            return (
              <div
                key={`symbolPicker-${this.props.action.id}-${p.name}-${index}`}
                className={p.isDeletedOnSource === true ? "isDeletedOnSource" : ""}
              >
                <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                  {p.name} ({p.Typename?.toString?.()})
                </span>
                <SymbolPicker
                  changeType="onBlur"
                  symbol={p.symbol}
                  valueType={p.Typename}
                  targetCardinality={p.cardinality}
                  onSelectedSymbol={(symbol, changeReason) =>
                    this.handleSelectSymbolPickerValue(p, symbol, changeReason)
                  }
                  bindingParameter={p}
                />
              </div>
            );
          })}
      </>
    );
  }

  handleSelectSymbolPickerValue = (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const params = _.cloneDeep(this.props.action.parameters);
    const param = params.find((param) => param.name === parameter.name);
    if (param) param.symbol = symbol;
    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;
    this.props.updateValues(valuesToUpdate, changeReason);
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const wfGenericProcessesSymbolsGroup = withContext<IWFGenericProcessesSymbolsGroupProps>(
  withSD<IWFGenericProcessesSymbolsGroupProps>(WFGenericProcessesSymbolsGroup)
);
export { wfGenericProcessesSymbolsGroup as WFGenericProcessesSymbolsGroup };
